/**
 * <cart-upsell> – dynamiske upsells i kurven ud fra Shopify Search & Discovery.
 *
 * Henter anbefalinger for hvert produkt i kurven (først "complementary", derefter evt. "related"),
 * fletter dem, fjerner dubletter og produkter der allerede ligger i kurven.
 * Prestige gen-renderer hele drawer'en ved hver kurv-ændring, så elementet oprettes forfra
 * og henter selv nye forslag. Svarene caches i hukommelsen for at undgå blink.
 */
const cache = new Map();

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.load();
  }

  fetchItems(productId, intent) {
    const url = `${this.dataset.url}?section_id=${this.dataset.sectionId}&product_id=${productId}&limit=10&intent=${intent}`;

    if (!cache.has(url)) {
      cache.set(
        url,
        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => {
            cache.delete(url);
            return '';
          })
      );
    }

    return cache.get(url).then((html) =>
      Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-upsell-item]'))
    );
  }

  async load() {
    const list = this.querySelector('[data-upsell-list]');
    const ids = (this.dataset.productIds || '').split(',').filter(Boolean).slice(0, 5);

    if (!list || ids.length === 0) {
      return;
    }

    const limit = parseInt(this.dataset.limit, 10) || 3;
    const intents = this.dataset.fallback === 'false' ? ['complementary'] : ['complementary', 'related'];
    const seen = new Set(ids);
    const items = [];

    for (const intent of intents) {
      const results = await Promise.all(ids.map((id) => this.fetchItems(id, intent)));
      const longest = Math.max(0, ...results.map((result) => result.length));

      // Flet resultaterne, så hvert produkt i kurven får sine bedste forslag med
      for (let i = 0; i < longest; i++) {
        for (const result of results) {
          const item = result[i];

          if (item && !seen.has(item.dataset.productId)) {
            seen.add(item.dataset.productId);
            items.push(item);
          }
        }
      }

      if (items.length >= limit) {
        break;
      }
    }

    if (items.length === 0 || !this.isConnected) {
      return;
    }

    list.replaceChildren(...items.slice(0, limit).map((item) => document.importNode(item, true)));
    this.hidden = false;
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
